/**
 * <product-gallery> — the PDP media column (sections/main-product.liquid), enhanced.
 *
 * HTML first: the featured image is a link to its full-size file and every thumbnail is a
 * link to its own, so without JS each opens the image. With JS a thumbnail swaps the main
 * image in place (a short cross-fade), and the main image opens a native <dialog> lightbox
 * with previous/next, a counter, arrow keys, Escape, backdrop click and a horizontal swipe.
 *
 * Interface (all read from the markup the section renders):
 *   [data-gallery-main]            the main <img>
 *   [data-gallery-open]            the link around it; click opens the lightbox at the current index
 *   [data-gallery-thumb]           one per image, in order: data-index, data-main (1200 px src),
 *                                  data-full (lightbox src), data-alt, data-w/data-h
 *   [data-gallery-lightbox]        the <dialog>; inside it [data-gallery-stage] <img>,
 *                                  [data-gallery-prev] [data-gallery-next] [data-gallery-close]
 *                                  and [data-gallery-counter] with data-template "%INDEX% / %COUNT%"
 *
 * Nothing here is product-specific: the section renders the same attributes for any product.
 */
if (!customElements.get('product-gallery')) {
  const SWIPE_MIN_PX = 40;

  customElements.define(
    'product-gallery',
    class ProductGallery extends HTMLElement {
      connectedCallback() {
        this.main = this.querySelector('[data-gallery-main]');
        this.opener = this.querySelector('[data-gallery-open]');
        this.thumbs = Array.from(this.querySelectorAll('[data-gallery-thumb]'));
        this.lightbox = this.querySelector('[data-gallery-lightbox]');
        this.images = this.thumbs.length
          ? this.thumbs.map((t) => ({ main: t.dataset.main, full: t.dataset.full, alt: t.dataset.alt || '', w: t.dataset.w, h: t.dataset.h }))
          : this.opener
            ? [{ main: this.main && this.main.getAttribute('src'), full: this.opener.getAttribute('href'), alt: (this.main && this.main.alt) || '' }]
            : [];
        this.index = 0;
        if (!this.main || !this.images.length) return;
        this.setAttribute('data-js', '');

        this.onThumb = (event) => {
          const thumb = event.target.closest('[data-gallery-thumb]');
          if (!thumb || !this.contains(thumb)) return;
          event.preventDefault();
          this.show(Number(thumb.dataset.index) || 0);
        };
        this.addEventListener('click', this.onThumb);

        if (this.opener && this.lightbox) {
          this.onOpen = (event) => {
            event.preventDefault();
            this.open(this.index);
          };
          this.opener.addEventListener('click', this.onOpen);
          this.wireLightbox();
        }
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.onThumb);
        if (this.opener && this.onOpen) this.opener.removeEventListener('click', this.onOpen);
        if (this.lightbox && this.onLightboxClick) {
          this.lightbox.removeEventListener('close', this.onClosed);
          this.lightbox.removeEventListener('click', this.onLightboxClick);
          this.lightbox.removeEventListener('keydown', this.onKey);
          this.lightbox.removeEventListener('pointerdown', this.onPointerDown);
          this.lightbox.removeEventListener('pointerup', this.onPointerUp);
          if (this.lightbox.open) this.lightbox.close();
        }
      }

      // Swap the main image: fade out, replace, fade in. The class carries the transition so
      // reduced-motion users get an instant swap from the stylesheet, not from a branch here.
      show(index) {
        const image = this.images[index];
        if (!image) return;
        this.index = index;
        this.main.classList.add('is-switching');
        const swap = () => {
          if (image.main) {
            this.main.removeAttribute('srcset');
            this.main.removeAttribute('sizes');
            this.main.src = image.main;
          }
          this.main.alt = image.alt;
          if (this.opener && image.full) this.opener.href = image.full;
          this.main.classList.remove('is-switching');
        };
        // One frame so the fade-out can paint; a missing rAF (tests) swaps at once.
        if (typeof requestAnimationFrame === 'function') requestAnimationFrame(swap);
        else swap();
        this.thumbs.forEach((thumb, i) => {
          if (i === index) thumb.setAttribute('aria-current', 'true');
          else thumb.removeAttribute('aria-current');
        });
      }

      wireLightbox() {
        this.stage = this.lightbox.querySelector('[data-gallery-stage]');
        this.counter = this.lightbox.querySelector('[data-gallery-counter]');
        this.onLightboxClick = (event) => {
          const t = event.target;
          if (t.closest('[data-gallery-close]')) return this.close();
          if (t.closest('[data-gallery-prev]')) return this.step(-1);
          if (t.closest('[data-gallery-next]')) return this.step(1);
          // The dialog element itself is only hit when the click lands on the backdrop.
          if (t === this.lightbox) this.close();
        };
        this.onKey = (event) => {
          // A modal <dialog> closes itself on Escape, but the fallback path below
          // (no showModal — the dialog is then just an [open] element) does not, so
          // Escape is handled here rather than left to the platform.
          if (event.key === 'Escape') {
            event.preventDefault();
            return this.close();
          }
          if (event.key === 'ArrowLeft') {
            event.preventDefault();
            this.step(-1);
          } else if (event.key === 'ArrowRight') {
            event.preventDefault();
            this.step(1);
          }
        };
        this.onPointerDown = (event) => {
          this.swipeX = event.clientX;
        };
        this.onPointerUp = (event) => {
          if (this.swipeX == null) return;
          const delta = event.clientX - this.swipeX;
          this.swipeX = null;
          if (Math.abs(delta) >= SWIPE_MIN_PX) this.step(delta < 0 ? 1 : -1);
        };
        // Escape closes a native <dialog> without going through close(), so the cleanup
        // hangs off the dialog's own event instead of the method — otherwise the fade class
        // would survive the close and the page would keep the pre-lightbox image.
        this.onClosed = () => {
          this.lightbox.classList.remove('is-open');
          this.show(this.index);
        };
        this.lightbox.addEventListener('close', this.onClosed);
        this.lightbox.addEventListener('click', this.onLightboxClick);
        this.lightbox.addEventListener('keydown', this.onKey);
        this.lightbox.addEventListener('pointerdown', this.onPointerDown);
        this.lightbox.addEventListener('pointerup', this.onPointerUp);
      }

      open(index) {
        this.render(index);
        if (typeof this.lightbox.showModal === 'function') this.lightbox.showModal();
        else this.lightbox.setAttribute('open', '');
        // Next frame, so the [open] → .is-open transition has a start state to run from.
        const reveal = () => this.lightbox.classList.add('is-open');
        if (typeof requestAnimationFrame === 'function') requestAnimationFrame(reveal);
        else reveal();
      }

      // The 'close' listener above does the cleanup, whichever way the dialog closes.
      close() {
        if (typeof this.lightbox.close === 'function') this.lightbox.close();
        else {
          this.lightbox.removeAttribute('open');
          this.onClosed();
        }
      }

      step(delta) {
        const count = this.images.length;
        this.render((this.index + delta + count) % count);
      }

      render(index) {
        const image = this.images[index];
        if (!image || !this.stage) return;
        this.index = index;
        this.stage.classList.add('is-switching');
        const swap = () => {
          this.stage.src = image.full || image.main;
          this.stage.alt = image.alt;
          // Carry each image's own dimensions: a stale width/height from the first image
          // gives the box the wrong ratio while the next one loads, which shows as a jump.
          if (image.w && image.h) {
            this.stage.width = image.w;
            this.stage.height = image.h;
          }
          this.stage.classList.remove('is-switching');
        };
        if (typeof requestAnimationFrame === 'function') requestAnimationFrame(swap);
        else swap();
        if (this.counter) {
          this.counter.textContent = (this.counter.dataset.template || '%INDEX% / %COUNT%')
            .replace('%INDEX%', index + 1)
            .replace('%COUNT%', this.images.length);
        }
        const single = this.images.length < 2;
        this.lightbox.querySelectorAll('[data-gallery-prev], [data-gallery-next]').forEach((b) => {
          b.hidden = single;
        });
      }
    }
  );
}
